// A list of short strings, edited one entry at a time: a row per entry with its remove (×)
// button, named after the entry, and an add field under them. Enter in the add field adds the
// entry (it never submits the form). Each entry is checked on its own, and the error shows under
// that row, or under the add field for the one being typed. An empty list reads "None".
import { useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { errorId, Fieldset, hintId } from "./field";
import type { HelpId } from "./help";

export interface ListEditorProps {
  /** The add field's id; rows and errors are named after it. */
  id: string;
  label: string;
  help: HelpId;
  values: readonly string[];
  onChange(next: string[]): void;
  /** Why one entry is not acceptable, or null. */
  check(value: string): string | null;
  hint?: ComponentChildren | undefined;
  /** The whole list's error (too many, listed twice), from the form's validation. */
  error?: string | undefined;
  disabled?: boolean | undefined;
  placeholder?: string | undefined;
  max?: number | null | undefined;
  class?: string | undefined;
}

export function rowErrorId(id: string, index: number): string {
  return `${id}-row-${index}-error`;
}

export function ListEditor({ id, label, help, values, onChange, check, hint, error, disabled, placeholder, max, class: className }: ListEditorProps) {
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const list = useRef<HTMLUListElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const full = max != null && values.length >= max;

  const add = () => {
    const value = draft.trim();
    if (value === "") return;
    const found = values.includes(value) ? "Already in the list." : check(value);
    setProblem(found);
    if (found) return;
    onChange([...values, value]);
    setDraft("");
  };

  const remove = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
    // Focus moves to the entry that took the removed one's place, else the add field.
    queueMicrotask(() => {
      const buttons = list.current?.querySelectorAll<HTMLButtonElement>("button") ?? [];
      (buttons[Math.min(index, buttons.length - 1)] ?? input.current)?.focus();
    });
  };

  const describedBy = [hint ? hintId(id) : "", problem ? errorId(id) : ""].filter(Boolean).join(" ") || undefined;
  return (
    <Fieldset label={label} help={help} error={error} class={className ? `list-editor ${className}` : "list-editor"}>
      {hint && (
        <span class="field-hint" id={hintId(id)}>
          {hint}
        </span>
      )}
      {values.length === 0 ? (
        <p class="list-editor-empty">None</p>
      ) : (
        <ul class="list-editor-rows" ref={list} aria-label={label}>
          {values.map((value, index) => {
            const rowProblem = check(value);
            return (
              <li key={`${index}:${value}`} class="list-editor-row">
                <span class="list-editor-value" aria-invalid={rowProblem ? true : undefined} aria-describedby={rowProblem ? rowErrorId(id, index) : undefined}>
                  {value}
                </span>
                <button type="button" class="list-editor-remove" aria-label={`Remove ${value}`} title={`Remove ${value}`} disabled={disabled} onClick={() => remove(index)}>
                  <span aria-hidden="true">×</span>
                </button>
                {rowProblem && (
                  <p class="field-error" id={rowErrorId(id, index)}>
                    {rowProblem}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <div class="list-editor-add">
        <input
          ref={input}
          id={id}
          type="text"
          class="input-mono"
          value={draft}
          placeholder={placeholder}
          aria-label={`Add to ${label}`}
          aria-invalid={problem ? true : undefined}
          aria-describedby={describedBy}
          autocomplete="off"
          disabled={disabled || full}
          onInput={(event) => {
            setDraft(event.currentTarget.value);
            setProblem(null);
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            add();
          }}
        />
        <button type="button" class="button button-sm" disabled={disabled || full || draft.trim() === ""} onClick={add}>
          Add
        </button>
      </div>
      {problem && (
        <p class="field-error" id={errorId(id)} role="alert">
          {problem}
        </p>
      )}
      {full && <span class="field-hint">The list is full ({max}).</span>}
    </Fieldset>
  );
}
